import React, {useEffect, useState} from 'react';
import {App, Button, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography,} from 'antd';
import {DeleteOutlined, EditOutlined, PauseCircleOutlined, PlayCircleOutlined, PlusOutlined,} from '@ant-design/icons';
import {CreateProxyRuleRequest, ProxyRule, ProxyServiceConfig} from '../../types/proxy';
import {proxyRuleService} from '../../services/proxyRuleService';
import {proxyService} from '../../services/proxyService';

const {Title} = Typography;
const {Option} = Select;
const {TextArea} = Input;

const ProxyRules: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const [rules, setRules] = useState<ProxyRule[]>([]);
    const [proxies, setProxies] = useState<ProxyServiceConfig[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRule, setEditingRule] = useState<ProxyRule | null>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        loadRules();
        loadProxies();
    }, []);

    const loadRules = async () => {
        setLoading(true);
        try {
            const data = await proxyRuleService.getAllRules();
            setRules(data);
        } catch (error) {
            messageApi.error('加载代理规则列表失败');
        } finally {
            setLoading(false);
        }
    };

    const loadProxies = async () => {
        try {
            const data = await proxyService.getActiveConfigs();
            setProxies(data);
        } catch (error) {
            messageApi.error('加载代理服务器列表失败');
        }
    };

    const handleCreate = () => {
        setEditingRule(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (rule: ProxyRule) => {
        setEditingRule(rule);
        form.setFieldsValue({
            ruleName: rule.ruleName,
            matchType: rule.matchType,
            matchPattern: rule.matchPattern,
            proxyId: rule.proxyId,
            status: rule.status,
            description: rule.description,
        });
        setModalVisible(true);
    };

    const handleDelete = async (ruleId: number) => {
        try {
            await proxyRuleService.deleteRule(ruleId);
            messageApi.success('删除成功');
            loadRules();
        } catch (error: any) {
            messageApi.error(error.response?.data?.message || error.message || '删除失败');
        }
    };

    const handleStatusChange = async (ruleId: number, status: 'active' | 'inactive') => {
        try {
            await proxyRuleService.setRuleStatus(ruleId, status);
            messageApi.success('状态更新成功');
            loadRules();
        } catch (error) {
            messageApi.error('状态更新失败');
        }
    };

    const handleSubmit = async (values: CreateProxyRuleRequest) => {
        try {
            if (editingRule?.ruleId) {
                await proxyRuleService.updateRule(editingRule.ruleId, values);
                messageApi.success('更新成功');
            } else {
                await proxyRuleService.createRule(values);
                messageApi.success('创建成功');
            }
            setModalVisible(false);
            loadRules();
        } catch (error: any) {
            messageApi.error(error.response?.data?.message || error.message || (editingRule ? '更新失败' : '创建失败'));
        }
    };

    const getMatchTypeTag = (type: string) => {
        switch (type) {
            case 'domain':
                return <Tag color="blue">域名</Tag>;
            case 'url':
                return <Tag color="green">URL</Tag>;
            case 'regex':
                return <Tag color="purple">正则</Tag>;
            case 'exchange':
                return <Tag color="orange">交易所</Tag>;
            default:
                return <Tag>{type}</Tag>;
        }
    };

    const getStatusTag = (status: string) => {
        return status === 'active' ? (
            <Tag color="green">激活</Tag>
        ) : (
            <Tag color="red">禁用</Tag>
        );
    };

    // 时间格式化
    const formatDateTime = (value: any) => {
        if (!value) return '-';
        if (typeof value === 'string') {
            return value.replace('T', ' ').substring(0, 19);
        }
        if (value instanceof Date) {
            return value.toISOString().replace('T', ' ').substring(0, 19);
        }
        return String(value);
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'ruleId',
            key: 'ruleId',
            width: 60,
        },
        {
            title: '规则名称',
            dataIndex: 'ruleName',
            key: 'ruleName',
            width: 150,
        },
        {
            title: '匹配类型',
            dataIndex: 'matchType',
            key: 'matchType',
            width: 100,
            render: (type: string) => getMatchTypeTag(type),
        },
        {
            title: '匹配模式',
            dataIndex: 'matchPattern',
            key: 'matchPattern',
            width: 200,
            ellipsis: true,
            render: (pattern: string) => (
                <Tooltip title={pattern}>
                    <span>{pattern}</span>
                </Tooltip>
            ),
        },
        {
            title: '代理服务器',
            dataIndex: 'proxyName',
            key: 'proxyName',
            width: 150,
            render: (_: any, record: ProxyRule) => (
                <span>{record.proxyName || `ID: ${record.proxyId}`}</span>
            ),
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            render: (status: string) => getStatusTag(status),
        },
        {
            title: '描述',
            dataIndex: 'description',
            key: 'description',
            width: 150,
            ellipsis: true,
        },
        {
            title: '创建时间',
            dataIndex: 'createdTime',
            key: 'createdTime',
            width: 160,
            render: (value: string) => formatDateTime(value),
        },
        {
            title: '操作',
            key: 'action',
            width: 180,
            fixed: 'right' as const,
            render: (_: any, record: ProxyRule) => (
                <Space size="small" wrap>
                    <Tooltip title={record.status === 'active' ? '禁用规则' : '启用规则'}>
                        <Button
                            type="link"
                            size="small"
                            icon={record.status === 'active' ? <PauseCircleOutlined/> : <PlayCircleOutlined/>}
                            onClick={() => record.ruleId && handleStatusChange(record.ruleId, record.status === 'active' ? 'inactive' : 'active')}
                        />
                    </Tooltip>
                    <Tooltip title="编辑规则">
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined/>}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="确定删除这个代理规则吗？"
                        description="删除后可通过恢复功能找回"
                        onConfirm={() => record.ruleId && handleDelete(record.ruleId)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Tooltip title="删除规则">
                            <Button
                                type="link"
                                size="small"
                                danger
                                icon={<DeleteOutlined/>}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
                <Title level={2}></Title>
                <Button type="primary" icon={<PlusOutlined/>} onClick={handleCreate}>
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={rules}
                rowKey="ruleId"
                loading={loading}
                pagination={{pageSize: 10}}
                scroll={{x: 1300, y: 400}}
                size="middle"
            />

            <Modal
                title={editingRule ? '编辑代理规则' : '新建代理规则'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        name="ruleName"
                        label="规则名称"
                        rules={[{required: true, message: '请输入规则名称'}]}
                    >
                        <Input placeholder="请输入规则名称"/>
                    </Form.Item>

                    <Form.Item
                        name="matchType"
                        label="匹配类型"
                        rules={[{required: true, message: '请选择匹配类型'}]}
                    >
                        <Select placeholder="请选择匹配类型">
                            <Option value="domain">域名</Option>
                            <Option value="url">URL路径</Option>
                            <Option value="regex">正则表达式</Option>
                            <Option value="exchange">交易所名称</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="matchPattern"
                        label="匹配模式"
                        rules={[{required: true, message: '请输入匹配模式'}]}
                        extra={
                            <span style={{color: 'var(--pt-text-secondary)', fontSize: 12}}>
                                域名示例: *.binance.com, api.binance.com<br/>
                                URL示例: /api/v1/*, /rest/*<br/>
                                正则示例: ^https?://.*\\.binance\\.com/.*<br/>
                                交易所示例: binance, okx, huobi
                            </span>
                        }
                    >
                        <Input placeholder="请输入匹配模式"/>
                    </Form.Item>

                    <Form.Item
                        name="proxyId"
                        label="代理服务器"
                        rules={[{required: true, message: '请选择代理服务器'}]}
                    >
                        <Select placeholder="请选择代理服务器" showSearch>
                            {proxies.map(proxy => (
                                <Option key={proxy.proxyId} value={proxy.proxyId!}>
                                    {proxy.proxyName} ({proxy.serverHost}:{proxy.serverPort})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="status"
                        label="状态"
                        rules={[{required: true, message: '请选择状态'}]}
                        initialValue="active"
                    >
                        <Select placeholder="请选择状态">
                            <Option value="active">激活</Option>
                            <Option value="inactive">禁用</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="描述"
                    >
                        <TextArea rows={3} placeholder="请输入规则描述"/>
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {editingRule ? '更新' : '创建'}
                            </Button>
                            <Button onClick={() => setModalVisible(false)}>
                                取消
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ProxyRules;